import { useState, useEffect } from "react";
import {
  getImapConfigs,
  createImapConfig,
  updateImapConfig,
  testImapConnection,
} from "../api";
import "./ImapConfigForm.css";

const PROVIDERS = {
  gmail: {
    name: "Gmail",
    host: "imap.gmail.com",
    port: 993,
    use_ssl: true,
    color: "#ea4335",
    icon: "\u2709",
    instructions: [
      '1. გახსენით Google ანგარიშის პარამეტრები: <a href="https://myaccount.google.com" target="_blank" rel="noreferrer">myaccount.google.com</a>',
      '2. გადადით \u201eუსაფრთხოება\u201c (Security) განყოფილებაში',
      '3. ჩართეთ \u201eორეტაპიანი ვერიფიკაცია\u201c (2-Step Verification) თუ არ არის ჩართული',
      '4. გახსენით App Passwords პირდაპირ ბმულით: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">myaccount.google.com/apppasswords</a>',
      '5. აპლიკაციის სახელად ჩაწერეთ \u201eNebula AI Hub\u201c და დააჭირეთ \u201eCreate\u201c',
      '6. გამოჩნდება 16-სიმბოლიანი პაროლი \u2014 დააკოპირეთ და ჩასვით ქვემოთ',
    ],
  },
  outlook: {
    name: "Outlook / Microsoft 365",
    host: "outlook.office365.com",
    port: 993,
    use_ssl: true,
    color: "#0078d4",
    icon: "\u2709",
    instructions: [
      '1. გახსენით Microsoft-ის ანგარიში: <a href="https://account.microsoft.com/security" target="_blank" rel="noreferrer">account.microsoft.com/security</a>',
      '2. გადადით \u201eუსაფრთხოება\u201c (Security) \u2192 \u201eდამატებითი უსაფრთხოების პარამეტრები\u201c: <a href="https://account.live.com/proofs/manage/additional" target="_blank" rel="noreferrer">გახსენით პირდაპირ</a>',
      '3. ჩართეთ \u201eორეტაპიანი ვერიფიკაცია\u201c (Two-step verification) თუ არ არის ჩართული',
      '4. იპოვეთ \u201eაპლიკაციის პაროლები\u201c (App Passwords) და შექმენით ახალი',
      '5. დააკოპირეთ გენერირებული პაროლი და ჩასვით ქვემოთ',
      '6. Microsoft 365-ის შემთხვევაში: ადმინმა უნდა ჩართოს IMAP წვდომა: <a href="https://admin.microsoft.com" target="_blank" rel="noreferrer">admin.microsoft.com</a>',
    ],
  },
};

export default function ImapConfigForm() {
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const [provider, setProvider] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [connected, setConnected] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showSmtp, setShowSmtp] = useState(false);
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUsername, setSmtpUsername] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");

  useEffect(() => {
    loadConfigs();
  }, []);

  async function loadConfigs() {
    setLoading(true);
    try {
      const data = await getImapConfigs();
      const list = Array.isArray(data) ? data : data.results || [];
      if (list.length > 0) {
        const c = list[0];
        setUsername(c.username);
        setDisplayName(c.display_name || "");
        setEditingId(c.id);
        setConnected(true);
        // Detect provider from host
        if (c.host === "imap.gmail.com") {
          setProvider("gmail");
        } else if (c.host === "outlook.office365.com") {
          setProvider("outlook");
        }
        // Load SMTP config
        if (c.smtp_host) {
          setSmtpHost(c.smtp_host);
          setSmtpPort(c.smtp_port || 587);
          setSmtpUsername(c.smtp_username || "");
          setShowSmtp(true);
        }
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  function selectProvider(key) {
    setProvider(key);
    setTestResult(null);
    setShowInstructions(true);
  }

  async function handleTest() {
    if (!provider) return;
    const p = PROVIDERS[provider];
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testImapConnection({
        host: p.host,
        port: p.port,
        username,
        password,
      });
      setTestResult({ success: true, message: result.message || "კავშირი წარმატებულია!" });
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    }
    setTesting(false);
  }

  async function handleSave() {
    if (!provider) return;
    const p = PROVIDERS[provider];
    const data = {
      host: p.host,
      port: p.port,
      username,
      password,
      display_name: displayName,
      folder: "INBOX",
      use_ssl: p.use_ssl,
      is_active: true,
      smtp_host: smtpHost || "",
      smtp_port: smtpPort || 587,
      smtp_username: smtpUsername || "",
      smtp_password: smtpPassword || "",
    };
    setSaving(true);
    try {
      if (editingId) {
        await updateImapConfig(editingId, data);
      } else {
        await createImapConfig(data);
      }
      setConnected(true);
      setTestResult({ success: true, message: "წარმატებით შეინახა!" });
      loadConfigs();
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    }
    setSaving(false);
  }

  function handleDisconnect() {
    setProvider(null);
    setUsername("");
    setPassword("");
    setDisplayName("");
    setEditingId(null);
    setConnected(false);
    setTestResult(null);
    setShowInstructions(false);
    setShowSmtp(false);
    setSmtpHost("");
    setSmtpPort(587);
    setSmtpUsername("");
    setSmtpPassword("");
  }

  if (loading) return <div>იტვირთება...</div>;

  return (
    <div className="imap-form">
      <h3 className="imap-title">ელ. ფოსტის დაკავშირება</h3>
      <p className="imap-desc">
        აირჩიეთ ელ. ფოსტის სერვისი, საიდანაც სისტემა წაიკითხავს შემოსულ წერილებს.
      </p>

      {/* Connected state */}
      {connected && provider && (
        <div className="imap-connected-box">
          <div className="imap-connected-info">
            <span
              className="imap-provider-dot"
              style={{ background: PROVIDERS[provider].color }}
            />
            <div>
              <div className="imap-connected-provider">{PROVIDERS[provider].name}</div>
              <div className="imap-connected-email">{displayName ? `${displayName} — ${username}` : username}</div>
              {smtpHost && (
                <div className="imap-connected-email" style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                  SMTP: {smtpUsername || smtpHost}
                </div>
              )}
            </div>
          </div>
          <button className="imap-disconnect-btn" onClick={handleDisconnect}>
            შეცვლა
          </button>
        </div>
      )}

      {/* Provider selection */}
      {!connected && !provider && (
        <div className="imap-providers">
          {Object.entries(PROVIDERS).map(([key, p]) => (
            <button
              key={key}
              className="imap-provider-btn"
              style={{ borderColor: p.color }}
              onClick={() => selectProvider(key)}
            >
              <span className="imap-provider-icon" style={{ color: p.color }}>
                {p.icon}
              </span>
              <span className="imap-provider-name">{p.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Setup form */}
      {!connected && provider && (
        <div className="imap-setup">
          <div className="imap-setup-header">
            <button
              className="imap-back-btn"
              onClick={() => { setProvider(null); setShowInstructions(false); setTestResult(null); }}
            >
              &larr; უკან
            </button>
            <span
              className="imap-setup-provider"
              style={{ color: PROVIDERS[provider].color }}
            >
              {PROVIDERS[provider].name}
            </span>
          </div>

          {/* Instructions */}
          <div className="imap-instructions">
            <button
              className="imap-instructions-toggle"
              onClick={() => setShowInstructions(!showInstructions)}
            >
              {showInstructions ? "\u25BC" : "\u25B6"} როგორ შევქმნა App Password?
            </button>
            {showInstructions && (
              <div className="imap-instructions-list">
                {PROVIDERS[provider].instructions.map((step, i) => (
                  <div key={i} className="imap-instruction-step" dangerouslySetInnerHTML={{ __html: step }} />
                ))}
              </div>
            )}
          </div>

          <div className="imap-fields">
            <div className="imap-field">
              <label>ელ. ფოსტა</label>
              <input
                type="email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={provider === "gmail" ? "example@gmail.com" : "example@outlook.com"}
              />
            </div>
            <div className="imap-field">
              <label>App Password (აპლიკაციის პაროლი)</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="ჩასვით აპლიკაციის პაროლი"
              />
            </div>
            <div className="imap-field">
              <label>გამგზავნის სახელი (არასავალდებულო)</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="მაგ: Chancellery AI"
              />
            </div>
          </div>

          {/* Separate SMTP config */}
          <div className="imap-smtp-section">
            <button
              type="button"
              className="imap-instructions-toggle"
              onClick={() => setShowSmtp(!showSmtp)}
            >
              {showSmtp ? "\u25BC" : "\u25B6"} სხვა SMTP გამგზავნის კონფიგურაცია (არასავალდებულო)
            </button>
            {showSmtp && (
              <div className="imap-fields" style={{ marginTop: 12 }}>
                <p className="imap-desc" style={{ fontSize: 13, marginBottom: 8 }}>
                  თუ გამგზავნი ელ. ფოსტა განსხვავდება მიმღებისგან, მიუთითეთ SMTP პარამეტრები.
                </p>
                <div className="imap-field">
                  <label>SMTP სერვერი</label>
                  <input
                    type="text"
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    placeholder="მაგ: smtp.office365.com"
                  />
                </div>
                <div className="imap-field">
                  <label>SMTP პორტი</label>
                  <input
                    type="number"
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(Number(e.target.value))}
                    placeholder="587"
                  />
                </div>
                <div className="imap-field">
                  <label>SMTP ელ. ფოსტა</label>
                  <input
                    type="email"
                    value={smtpUsername}
                    onChange={(e) => setSmtpUsername(e.target.value)}
                    placeholder="მაგ: info@example.com"
                  />
                </div>
                <div className="imap-field">
                  <label>SMTP პაროლი</label>
                  <input
                    type="password"
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    placeholder="SMTP აპლიკაციის პაროლი"
                  />
                </div>
              </div>
            )}
          </div>

          {testResult && (
            <div className={`imap-test-result ${testResult.success ? "success" : "error"}`}>
              {testResult.message}
            </div>
          )}

          <div className="imap-actions">
            <button
              className="imap-test-btn"
              onClick={handleTest}
              disabled={testing || !username || !password}
            >
              {testing ? "მოწმდება..." : "კავშირის შემოწმება"}
            </button>
            <button
              className="imap-save-btn"
              onClick={handleSave}
              disabled={saving || !username || !password}
            >
              {saving ? "ინახება..." : "დაკავშირება"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
